/**
 * ModelFallbackFactory — a PONTE entre o pipeline GLB e os modelos procedurais.
 *
 * `createVisual(key)` tenta instanciar o GLB cacheado; se existir, devolve um RiggedVisual
 * (modelo + AnimationController + sockets + materiais para flash) já com escala/rotação/offset
 * do ModelRegistry e um passe de identidade VOID aplicado. Se o GLB não existe/falha,
 * devolve `null` — e quem chamou (Player/Enemy) usa o rig procedural atual (§12 Fallback).
 *
 * Enquanto nenhum GLB estiver presente, TODAS as chamadas devolvem null → nada é "migrado".
 */
import * as THREE from "three";
import { modelLoader } from "./ModelLoader";
import { modelEntry, type ModelEntry } from "./ModelRegistry";
import { AnimationController } from "./AnimationController";
import { EquipmentAttachmentSystem } from "./EquipmentAttachmentSystem";

export interface IdentityTint {
  /** cor base (mesclada levemente na cor do modelo cru). */
  color?: number;
  /** 0..1 quanto puxar a cor original para `color` (default 0.35). */
  tintAmount?: number;
  /** brilho de energia (emissive) da identidade VOID. */
  emissive?: number;
  /** intensidade do emissive de repouso. */
  emissiveIntensity?: number;
  roughness?: number;
  metalness?: number;
}

export interface RiggedVisual {
  readonly root: THREE.Object3D;
  readonly entry: ModelEntry;
  readonly anim: AnimationController;
  readonly sockets: EquipmentAttachmentSystem;
  /** Materiais standard clonados por instância (permite flash de dano sem afetar outros). */
  readonly bodyMats: THREE.MeshStandardMaterial[];
  update(dt: number): void;
  dispose(): void;
}

/** Clona materiais por instância e aplica a identidade VOID (emissive/tint). Retorna os standard. */
function applyIdentity(root: THREE.Object3D, tint?: IdentityTint): THREE.MeshStandardMaterial[] {
  const out: THREE.MeshStandardMaterial[] = [];
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.material) return;
    const cloneOne = (m: THREE.Material): THREE.Material => {
      const c = m.clone();
      if ((c as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
        const sm = c as THREE.MeshStandardMaterial;
        if (tint?.color !== undefined) sm.color.lerp(new THREE.Color(tint.color), tint.tintAmount ?? 0.35);
        if (tint?.emissive !== undefined) {
          sm.emissive = new THREE.Color(tint.emissive);
          sm.emissiveIntensity = tint.emissiveIntensity ?? 0.35;
        }
        if (tint?.roughness !== undefined) sm.roughness = tint.roughness;
        if (tint?.metalness !== undefined) sm.metalness = tint.metalness;
        out.push(sm);
      }
      return c;
    };
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(cloneOne) : cloneOne(mesh.material);
  });
  return out;
}

/** Cria o visual rigado para uma chave do registry, ou null (→ fallback procedural). */
export function createVisual(key: string, tint?: IdentityTint): RiggedVisual | null {
  const entry = modelEntry(key);
  if (!entry) return null;
  const inst = modelLoader.instantiate(key);
  if (!inst) return null;

  const root = inst.root;
  root.scale.setScalar(entry.scale);
  root.rotation.y = entry.rotationY;
  root.position.y = entry.yOffset;

  // identidade: override do registry (§35) tem precedência sobre o tint de runtime.
  const mo = entry.materialOverrides;
  const effTint: IdentityTint = {
    ...tint,
    ...(mo?.tintColor !== undefined ? { color: mo.tintColor } : {}),
    ...(mo?.tintAmount !== undefined ? { tintAmount: mo.tintAmount } : {}),
    ...(mo?.emissive !== undefined ? { emissive: mo.emissive } : {}),
    ...(mo?.emissiveIntensity !== undefined ? { emissiveIntensity: mo.emissiveIntensity } : {}),
    ...(mo?.roughness !== undefined ? { roughness: mo.roughness } : {}),
    ...(mo?.metalness !== undefined ? { metalness: mo.metalness } : {}),
  };
  const bodyMats = applyIdentity(root, effTint);
  const anim = new AnimationController(root, inst.clips, entry.animationMap);
  const sockets = new EquipmentAttachmentSystem(root, entry.sockets);

  return {
    root,
    entry,
    anim,
    sockets,
    bodyMats,
    update: (dt: number) => anim.update(dt),
    dispose: () => {
      anim.dispose();
      sockets.dispose();
      root.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh && !m.geometry.userData.shared) m.geometry.dispose();
      });
      for (const mat of bodyMats) mat.dispose();
    },
  };
}

/** Conveniência: visual rigado do Hunter (ou null). */
export function createPlayerVisual(tint?: IdentityTint): RiggedVisual | null {
  return createVisual("player_hunter", tint);
}

/** Conveniência: visual rigado de um inimigo pelo def.id (ou null). */
export function createEnemyVisual(defId: string, tint?: IdentityTint): RiggedVisual | null {
  return createVisual(defId, tint);
}
